import {
  keepPreviousData,
  useInfiniteQuery,
  useQuery,
  type UseQueryOptions,
} from '@tanstack/react-query';

import type {
  GetDesignationListParams,
  GetDesignationListResponse,
} from '@/types/designation';
import { getDesignationList } from '@/api/designation';
import { useAuth } from '@/context/AuthContext';
import { mergeQueryOptions } from '@/utility/queryOptions';
import { QueryKeys } from '@gokrew/models/constants';
import { fetchAllPages } from '@gokrew/ui/utility/fetchAllPages';

export function useDesignations(
  params: GetDesignationListParams,
  options: Omit<
    UseQueryOptions<GetDesignationListResponse>,
    'queryKey' | 'queryFn'
  > = {}
) {
  const { companyId, branchId: authBranchId } = useAuth();
  const effectiveBranchId = params.branchId ?? authBranchId;
  return useQuery<GetDesignationListResponse>({
    queryKey: [QueryKeys.Designations, params, effectiveBranchId],
    queryFn: () =>
      getDesignationList(companyId!, {
        ...params,
        ...(effectiveBranchId ? { branchId: effectiveBranchId } : {}),
      }),
    placeholderData: keepPreviousData,
    ...mergeQueryOptions(options, [!!companyId]),
  });
}

export const useInfiniteDesignations = (
  params: Omit<GetDesignationListParams, 'page'>
) => {
  const { companyId, branchId: authBranchId } = useAuth();
  const effectiveBranchId = params.branchId ?? authBranchId;
  return useInfiniteQuery({
    queryKey: [QueryKeys.Designations, 'infinite', params, effectiveBranchId],
    queryFn: ({ pageParam }) =>
      getDesignationList(companyId!, {
        ...params,
        page: pageParam,
        ...(effectiveBranchId ? { branchId: effectiveBranchId } : {}),
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.data.meta.hasNextPage ? lastPage.data.meta.page + 1 : undefined,
    enabled: !!companyId,
  });
};

/**
 * Every job title (all pages), for screens that list or name them all:
 * a company can have more than one page of them.
 */
export function useAllDesignations(
  params: Omit<GetDesignationListParams, 'page' | 'limit'> = {},
  options: { enabled?: boolean } = {}
) {
  const { companyId, branchId: authBranchId } = useAuth();
  const effectiveBranchId = params.branchId ?? authBranchId;
  return useQuery<GetDesignationListResponse>({
    queryKey: [QueryKeys.Designations, 'all', params, effectiveBranchId],
    queryFn: () =>
      fetchAllPages((page, limit) =>
        getDesignationList(companyId!, {
          ...params,
          page,
          limit,
          ...(effectiveBranchId ? { branchId: effectiveBranchId } : {}),
        })
      ),
    enabled: !!companyId && (options.enabled ?? true),
  });
}
